/**
 * PourContactsSection - Manage Pour2Contact records for a pour
 *
 * UI mirrors PourCreate's Notifications / Alerts step (same table with
 * directly-toggleable Email / Alert checkboxes, same "ADD A CONTACT" button
 * and add dialog) so the create and edit flows read identically; changes
 * here save immediately via the pour-contacts API instead of being staged.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Paper,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  poursAPI,
  type Pour2Contact,
  type PourLookups,
} from '../../services/pours';
import { apiErrorText } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PourContactsSectionProps {
  pourId: string;
  mode: 'create' | 'edit' | 'view';
  lookups: PourLookups;
}

const flagToBool = (flag?: string): boolean => flag === 'Y';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PourContactsSection: React.FC<PourContactsSectionProps> = ({
  pourId,
  mode,
  lookups,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isViewMode = mode === 'view';

  const [contacts, setContacts] = useState<Pour2Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newContactId, setNewContactId] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Pour2Contact | null>(null);
  const [deleting, setDeleting] = useState(false);

  // =====================================================================
  // Data loading
  // =====================================================================

  const loadContacts = useCallback(async () => {
    if (!pourId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await poursAPI.getPourContacts(pourId);
      setContacts(data);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to load contacts'));
    } finally {
      setLoading(false);
    }
  }, [pourId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Contacts not yet on the pour (drives the Add dialog + button state).
  const availableContacts = lookups.contacts.filter(
    (c) => !contacts.some((added) => added.ContactID === c.id),
  );

  // =====================================================================
  // Inline toggles — save immediately, optimistic update with rollback
  // =====================================================================

  const toggleField = async (contact: Pour2Contact, field: 'SendEmail' | 'Alert') => {
    if (isViewMode) return;
    const nextFlag = flagToBool(contact[field]) ? 'N' : 'Y';
    setContacts((prev) =>
      prev.map((c) => (c.ID === contact.ID ? { ...c, [field]: nextFlag } : c)),
    );
    setError(null);
    try {
      await poursAPI.updatePourContact(pourId, contact.ID, { [field]: nextFlag });
    } catch (err: any) {
      // Roll back the optimistic flip
      setContacts((prev) =>
        prev.map((c) => (c.ID === contact.ID ? { ...c, [field]: contact[field] } : c)),
      );
      setError(apiErrorText(err, 'Failed to update contact'));
    }
  };

  // =====================================================================
  // Add handlers
  // =====================================================================

  const handleAdd = async () => {
    if (!newContactId) return;
    setSaving(true);
    setError(null);
    try {
      await poursAPI.addPourContact(pourId, {
        ContactID: newContactId,
        SendEmail: 'Y',
        Alert: 'Y',
      });
      setAddDialogOpen(false);
      setNewContactId('');
      await loadContacts();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to add contact'));
    } finally {
      setSaving(false);
    }
  };

  // =====================================================================
  // Delete handlers
  // =====================================================================

  const handleDeleteClick = (contact: Pour2Contact) => {
    setContactToDelete(contact);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await poursAPI.deletePourContact(pourId, contactToDelete.ID);
      setDeleteConfirmOpen(false);
      setContactToDelete(null);
      await loadContacts();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to delete contact'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setContactToDelete(null);
  };

  // =====================================================================
  // Helpers
  // =====================================================================

  const getContactName = (contactId: string): string => {
    const found = lookups.contacts.find((c) => c.id === contactId);
    return found ? found.name : contactId;
  };

  const contactLabel = (contact: Pour2Contact): string =>
    contact.ContactName || getContactName(contact.ContactID);

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : contacts.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">
                No contacts added. Tap &ldquo;Add a contact&rdquo; below to send notifications.
              </Typography>
            </Paper>
          ) : (
            contacts.map((contact) => (
              <Card key={contact.ID} elevation={1}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {contactLabel(contact)}
                      </Typography>
                      {contact.Email && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {contact.Email}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label="Email"
                          size="small"
                          variant={flagToBool(contact.SendEmail) ? 'filled' : 'outlined'}
                          color={flagToBool(contact.SendEmail) ? 'primary' : 'default'}
                          onClick={isViewMode ? undefined : () => toggleField(contact, 'SendEmail')}
                        />
                        <Chip
                          label="Alerts"
                          size="small"
                          variant={flagToBool(contact.Alert) ? 'filled' : 'outlined'}
                          color={flagToBool(contact.Alert) ? 'primary' : 'default'}
                          onClick={isViewMode ? undefined : () => toggleField(contact, 'Alert')}
                        />
                      </Box>
                    </Box>
                    {!isViewMode && (
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(contact)} sx={{ flexShrink: 0 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {!isViewMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Tap a chip to toggle.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">Alert</TableCell>
                {!isViewMode && <TableCell width={50} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No contacts added. Click the button below to add notification contacts.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.ID}>
                    <TableCell>
                      {contactLabel(contact)}
                      {contact.Email && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {contact.Email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={flagToBool(contact.SendEmail)}
                        onChange={() => toggleField(contact, 'SendEmail')}
                        size="small"
                        disabled={isViewMode}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={flagToBool(contact.Alert)}
                        onChange={() => toggleField(contact, 'Alert')}
                        size="small"
                        disabled={isViewMode}
                      />
                    </TableCell>
                    {!isViewMode && (
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(contact)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isViewMode && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewContactId('');
            setAddDialogOpen(true);
          }}
          disabled={loading || availableContacts.length === 0}
          fullWidth={isMobile}
          sx={{ textTransform: 'none', ...(isMobile && { py: 1.25 }) }}
        >
          ADD A CONTACT
        </Button>
      )}

      {/* Contact Selector Dialog — mirrors PourCreate's */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Contact</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl size="small" required>
              <InputLabel>Contact</InputLabel>
              <Select
                value={newContactId}
                label="Contact"
                onChange={(e: SelectChangeEvent) => setNewContactId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableContacts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!newContactId || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('pourContacts.confirmDelete', 'Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pourContacts.confirmDeleteMessage', 'Are you sure you want to remove this contact from the pour?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('pourContacts.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {t('pourContacts.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourContactsSection;
